import { Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormRecord,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { DropdownData, GenericDropdownComponent, CurrencyInputFieldComponent } from '@zayan-erp/shared';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { InputNumber } from '@openng/optimus-ui/inputnumber';
import {
  DsmProductAttributeGroup,
  DsmProductOption,
  DsmSelectedProductValues,
  ProjectModelRoomQuantityLookup
} from '../../../models/dsm-deal-options.models';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';
import { rxResourceFixed } from '@zayan-erp/shared';
import { Message } from '@openng/optimus-ui/message';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@openng/optimus-ui/tabs';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { map, NEVER, of, startWith } from 'rxjs';
import { LocationItem } from '@zayan-erp/home-building';

export type LocationFormGroup = FormGroup<{
  locationId: FormControl<string | null>;
  locationName: FormControl<string>;
  quantity: FormControl<number | null>;
  unitPrice: FormControl<number | null>;
  notes: FormControl<string>;
  attributeValues: FormRecord<FormControl<string | null>>;
}>;

@Component({
  selector: 'dsm-selected-product-multi-location-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    GenericDropdownComponent,
    TextareaModule,
    CurrencyInputFieldComponent,
    InputNumber,
    Message,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    ButtonModule,
    NgTemplateOutlet,
    FloatLabelModule
  ],
  templateUrl: './dsm-selected-product-multi-location-form.html',
  styleUrl: './dsm-selected-product-multi-location-form.component.scss'
})
export class DsmSelectedProductMultiLocationFormComponent {
  readonly #modelElevationProductsService = inject(DsmModelElevationProductsService);
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);

  readonly product = input.required<DsmProductOption>();
  readonly addendumId = input<string | null>(null);
  // Location source before an addendum exists (appointment screens); ignored when addendumId is set.
  readonly lotId = input<string | null>(null);
  readonly preloadedLocations = input<LocationItem[] | null>();
  readonly commit = output<DsmSelectedProductValues[]>();

  readonly locationIdsControl = this.#fb.control<string[]>([]);

  readonly selectedLocations: FormArray<LocationFormGroup> = this.#fb.array<LocationFormGroup>([]);

  readonly submitAttempted = signal(false);
  readonly activeLocationId = signal<string | null>(null);

  readonly noMeasurementPerLocation = signal<Record<string, boolean>>({});

  readonly #locationIdsValue = toSignal(
    this.locationIdsControl.valueChanges.pipe(startWith(this.locationIdsControl.value ?? [])),
    { initialValue: [] as string[] }
  );

  readonly #locationsSnapshot = toSignal(
    this.selectedLocations.valueChanges.pipe(startWith(this.selectedLocations.getRawValue())),
    { initialValue: [] as ReturnType<typeof this.selectedLocations.getRawValue> }
  );

  readonly locationOptions = rxResourceFixed({
    params: () => ({
      productId: this.product().productId,
      addendumId: this.addendumId(),
      lotId: this.lotId(),
      preloaded: this.preloadedLocations()
    }),
    defaultValue: [] as DropdownData[],
    stream: ({ params }) => {
      if (params.preloaded === null) return NEVER;
      if (params.preloaded)
        return of(
          params.preloaded.map(location => ({
            id: location.id,
            firstLabel: location.locationCode ?? '—',
            secondLabel: location.locationName
          }))
        );
      if (!params.productId) return of<DropdownData[]>([]);
      return this.#modelElevationProductsService.getScopedProductLocations(params, params.productId).pipe(
        map(locations =>
          locations.map(location => ({
            id: location.id,
            firstLabel: location.locationCode ?? '—',
            secondLabel: location.locationName
          }))
        )
      );
    }
  });

  readonly productAttributesResource = rxResourceFixed({
    params: () => ({ productId: this.product().productId }),
    defaultValue: [] as DsmProductAttributeGroup[],
    stream: ({ params }) => {
      if (!params.productId) return of<DsmProductAttributeGroup[]>([]);
      return this.#modelElevationProductsService.getProductAttributes(params.productId);
    }
  });

  readonly productAttributes = computed(() => this.productAttributesResource.value() ?? []);

  readonly hasAttributeConfigError = computed(
    () =>
      this.product().selectionRequired &&
      (this.productAttributesResource.status() === 'idle' || this.productAttributesResource.status() === 'resolved') &&
      this.productAttributes().length === 0
  );

  readonly attributeValueOptions = computed(() => {
    const result = new Map<string, DropdownData[]>();
    this.productAttributes().forEach(attribute => {
      result.set(
        attribute.subCategoryAttributeId,
        attribute.productAttributeValues.map(value => ({ id: value.id, firstLabel: value.value }))
      );
    });
    return result;
  });

  // Derived list used by the template — reactive via #locationsSnapshot
  readonly selectedLocationList = computed(() => {
    void this.#locationsSnapshot();
    return this.selectedLocations.controls.map((group, index) => ({
      index,
      locationId: group.controls.locationId.value,
      locationName: group.controls.locationName.value,
      group
    }));
  });

  // The snapshot is typed as a union of the raw/partial row arrays, so `reduce` can't infer a numeric accumulator.
  readonly #locationTotalsRows = computed<{ quantity?: number | null; unitPrice?: number | null }[]>(() =>
    this.#locationsSnapshot()
  );

  readonly totalQty = computed(() => this.#locationTotalsRows().reduce((sum, loc) => sum + (loc.quantity ?? 0), 0));

  readonly totalValue = computed(() =>
    this.#locationTotalsRows().reduce((sum, loc) => sum + (loc.quantity ?? 0) * (loc.unitPrice ?? 0), 0)
  );

  readonly locationCount = computed(() => {
    this.#locationsSnapshot();
    return this.selectedLocations.length;
  });

  constructor() {
    effect(() => {
      const ids = this.#locationIdsValue() ?? [];
      const options = this.locationOptions.value();

      const currentIds = this.selectedLocations.controls.map(g => g.controls.locationId.value);

      ids.forEach(id => {
        if (!currentIds.includes(id)) {
          const option = options.find(o => o.id === id);
          const locationName = option?.secondLabel ?? option?.firstLabel ?? '';
          const group = this.#createLocationGroup(id, locationName);
          this.selectedLocations.push(group);
          if (this.activeLocationId() === null) {
            this.activeLocationId.set(id);
          }
          this.#fetchRoomQuantity(id, group);
        }
      });

      for (let i = this.selectedLocations.length - 1; i >= 0; i--) {
        const group = this.selectedLocations.at(i);
        const removedId = group.controls.locationId.value;
        if (removedId === null || !ids.includes(removedId)) {
          this.selectedLocations.removeAt(i);
          this.noMeasurementPerLocation.update(rec => {
            const updated = { ...rec };
            if (removedId) delete updated[removedId];
            return updated;
          });

          if (this.activeLocationId() === removedId) {
            const first = this.selectedLocations.at(0);
            this.activeLocationId.set(first ? first.controls.locationId.value : null);
          }
        }
      }

      if (this.selectedLocations.length === 0) {
        this.selectedLocations.push(this.#createLocationGroup(null, ''));
      }
    });

    effect(() => {
      const attributes = this.productAttributes();
      this.#locationsSnapshot();
      if (!attributes.length) return;

      const requiredByProduct = this.product().selectionRequired;

      this.selectedLocations.controls.forEach(locationGroup => {
        const formRecord = locationGroup.controls.attributeValues;
        const controls = formRecord.controls;
        const attributeIds = new Set(attributes.map(a => a.subCategoryAttributeId));

        attributes.forEach(attribute => {
          if (!controls[attribute.subCategoryAttributeId]) {
            const control = new FormControl<string | null>(null, {
              validators: requiredByProduct ? [Validators.required] : []
            });
            if (this.submitAttempted()) control.markAsTouched();
            formRecord.addControl(attribute.subCategoryAttributeId, control);
          }
        });

        Object.keys(controls).forEach(name => {
          if (!attributeIds.has(name)) formRecord.removeControl(name);
        });
      });
    });
  }

  getAttributeValuesGroup(group: LocationFormGroup): FormRecord<FormControl<string | null>> {
    return group.controls.attributeValues;
  }

  attemptCommit(): void {
    this.submitAttempted.set(true);
    this.selectedLocations.markAllAsTouched();

    if (!this.selectedLocations.length || this.selectedLocations.invalid || this.hasAttributeConfigError()) return;

    const values: DsmSelectedProductValues[] = this.selectedLocations.controls.map(group => {
      const raw = group.getRawValue();
      const attributeValues: Record<string, string> = {};
      Object.entries(group.controls.attributeValues.controls).forEach(([id, control]) => {
        if (control.value) attributeValues[id] = control.value;
      });
      return {
        locationId: raw.locationId,
        quantity: raw.quantity ?? 0,
        unitPrice: raw.unitPrice ?? 0,
        notes: raw.notes ?? '',
        attributeValues
      };
    });

    this.commit.emit(values);
  }

  applyCurrentToAllLocations(): void {
    const activeId = this.activeLocationId();
    if (!activeId) return;

    const sourceGroup = this.selectedLocations.controls.find(g => g.controls.locationId.value === activeId);
    if (!sourceGroup) return;

    const sourceRaw = sourceGroup.getRawValue();
    const sourceAttrs = sourceGroup.controls.attributeValues.controls;

    this.selectedLocations.controls.forEach(group => {
      if (group.controls.locationId.value === activeId) return;
      group.controls.quantity.setValue(sourceRaw.quantity);
      group.controls.unitPrice.setValue(sourceRaw.unitPrice);
      group.controls.notes.setValue(sourceRaw.notes);

      const targetAttrs = group.controls.attributeValues.controls;
      Object.entries(sourceAttrs).forEach(([id, control]) => {
        if (targetAttrs[id]) targetAttrs[id].setValue(control.value);
      });
    });
  }

  #fetchRoomQuantity(locationId: string | null, group: LocationFormGroup): void {
    if (!locationId) return;
    const product = this.product();
    const addendumId = this.addendumId();
    if (!addendumId || !product.productId || !product.uofmId) return;

    this.#modelElevationProductsService
      .getRoomQuantityLookup(addendumId, product.productId, locationId, product.uofmId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((result: ProjectModelRoomQuantityLookup) => {
        const noMeasurement = result.matchedShape === 'None';
        this.noMeasurementPerLocation.update(rec => ({ ...rec, [locationId]: noMeasurement }));
        if (result.quantity !== null) {
          group.controls.quantity.setValue(result.quantity, { emitEvent: true });
        }
      });
  }

  #createLocationGroup(locationId: string | null, locationName: string): LocationFormGroup {
    const product = this.product();
    return this.#fb.group({
      locationId: this.#fb.control<string | null>(locationId),
      locationName: this.#fb.nonNullable.control(locationName),
      quantity: this.#fb.control<number | null>(product.quantity ?? null, [Validators.required]),
      unitPrice: this.#fb.control<number | null>(product.unitPrice ?? null, [Validators.required]),
      notes: this.#fb.nonNullable.control(''),
      attributeValues: this.#fb.record<FormControl<string | null>>({})
    }) as LocationFormGroup;
  }
}
